import { ORGANIZATION_API_URLS } from "@/config/apiConfig";
import {
  CreateOrganizationPayload,
  UpdateOrganizationPayload,
} from "@/types/organization";

const getAuthToken = (): string => {
  if (typeof window === "undefined") return "";
  return localStorage.getItem("token") || "";
};

export const getAllOrganizations = async (): Promise<any[]> => {
  const token = getAuthToken();
  const response = await fetch(`${ORGANIZATION_API_URLS.GET_ALL_ORGANIZATIONS}?limit=500`, {
    method: "GET",
    headers: { "Content-Type": "application/json", Authorization: token },
  });
  if (!response.ok) throw new Error("Failed to get all organizations");
  const result = await response.json();
  const items = result?.data?.items;
  return Array.isArray(items) ? items : [];
};

/** No single-item endpoint — filters the list by name. */
export const getOrganizationById = async (organizationId: string): Promise<any> => {
  const token = getAuthToken();
  const filters = encodeURIComponent(JSON.stringify([["name", "=", organizationId]]));
  const response = await fetch(
    `${ORGANIZATION_API_URLS.GET_ALL_ORGANIZATIONS}?filters=${filters}&limit=1`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json", Authorization: token },
    }
  );
  if (!response.ok) throw new Error("Failed to get organization by id");
  const result = await response.json();
  const items = result?.data?.items;
  return Array.isArray(items) && items.length > 0 ? items[0] : null;
};

export const createOrganization = async (
  organizationData: CreateOrganizationPayload
): Promise<any> => {
  const token = getAuthToken();
  const response = await fetch(ORGANIZATION_API_URLS.CREATE_ORGANIZATION, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: token },
    body: JSON.stringify(organizationData),
  });
  if (!response.ok) throw new Error("Failed to create organization");
  const result = await response.json();
  return result?.data?.item ?? result?.data ?? result;
};

export const updateOrganization = async (
  organizationId: string,
  organizationData: UpdateOrganizationPayload
): Promise<any> => {
  const token = getAuthToken();
  const response = await fetch(ORGANIZATION_API_URLS.UPDATE_ORGANIZATION, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: token },
    body: JSON.stringify({ name: organizationId, ...organizationData }),
  });
  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = "Failed to update organization";
    try {
      const errorData = JSON.parse(errorText);
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }
  const result = await response.json();
  return result?.data?.item ?? result?.data ?? result;
};

export const deleteOrganization = async (organizationId: string): Promise<any> => {
  const token = getAuthToken();
  const response = await fetch(ORGANIZATION_API_URLS.DELETE_ORGANIZATION, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: token },
    body: JSON.stringify({ name: organizationId }),
  });
  if (!response.ok) throw new Error("Failed to delete organization");
  const result = await response.json();
  return result?.data ?? result;
};
